import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import type { TemplateBuilderConfig } from '@/types/template-builder';
import type { StatementDocumentData } from './statement-data';

// Disable automatic word hyphenation so long words wrap to the next line
// instead of being split with a hyphen.
Font.registerHyphenationCallback((word) => [word]);

// ---- Constants ------------------------------------------------------------

const PAGE_SIZE_MAP: Record<string, string> = {
  a4: 'A4',
  a5: 'A5',
  letter: 'LETTER',
};

const MARGIN_MM: Record<string, number> = {
  narrow: 15,
  normal: 25,
  wide: 35,
};

const LOGO_HEIGHT: Record<string, number> = {
  small: 24,
  medium: 36,
  large: 50,
};

// Map UI font names to standard PDF font families.
// @react-pdf/renderer supports Helvetica, Times-Roman, and Courier out of the box.
const FONT_MAP: Record<string, { regular: string; bold: string }> = {
  Arial: { regular: 'Helvetica', bold: 'Helvetica-Bold' },
  Calibri: { regular: 'Helvetica', bold: 'Helvetica-Bold' },
  Helvetica: { regular: 'Helvetica', bold: 'Helvetica-Bold' },
  Georgia: { regular: 'Times-Roman', bold: 'Times-Bold' },
  'Times New Roman': { regular: 'Times-Roman', bold: 'Times-Bold' },
};

function mapFont(font: string): { regular: string; bold: string } {
  return FONT_MAP[font] ?? { regular: 'Helvetica', bold: 'Helvetica-Bold' };
}

const ACCOUNT_SUMMARY_KEYS = [
  'totalOutstanding',
  'totalOverdue',
  'totalNotYetDue',
  'creditLimit',
  'availableCredit',
  'lastPaymentAmount',
  'lastPaymentDate',
] as const;

type AccountSummaryKey = (typeof ACCOUNT_SUMMARY_KEYS)[number];

const ACCOUNT_SUMMARY_LABELS: Record<AccountSummaryKey, string> = {
  totalOutstanding: 'Total Outstanding',
  totalOverdue: 'Total Overdue',
  totalNotYetDue: 'Total Not Yet Due',
  creditLimit: 'Credit Limit',
  availableCredit: 'Available Credit',
  lastPaymentAmount: 'Last Payment',
  lastPaymentDate: 'Last Payment Date',
};

const ACCOUNT_SUMMARY_DATA_KEYS: Record<AccountSummaryKey, keyof StatementDocumentData> = {
  totalOutstanding: 'totalOutstanding',
  totalOverdue: 'totalOverdue',
  totalNotYetDue: 'totalNotYetDue',
  creditLimit: 'creditLimit',
  availableCredit: 'availableCredit',
  lastPaymentAmount: 'lastPaymentAmount',
  lastPaymentDate: 'lastPaymentDate',
};

const TOTALS_KEYS = ['totalInvoiced', 'totalOutstanding', 'totalOverdue', 'totalNotYetDue'] as const;

type TotalsKey = (typeof TOTALS_KEYS)[number];

const TOTAL_LABELS: Record<TotalsKey, string> = {
  totalInvoiced: 'Total Invoiced',
  totalOutstanding: 'Total Outstanding',
  totalOverdue: 'Total Overdue',
  totalNotYetDue: 'Total Not Yet Due',
};

const TOTAL_DATA_KEYS: Record<TotalsKey, keyof StatementDocumentData> = {
  totalInvoiced: 'totalInvoiced',
  totalOutstanding: 'totalOutstanding',
  totalOverdue: 'totalOverdue',
  totalNotYetDue: 'totalNotYetDue',
};

const COLUMN_FLEX: Record<string, number> = {
  invoice_no: 1.2,
  invoice_date: 1,
  due_date: 1,
  description: 2,
  invoice_amount: 1,
  balance_outstanding: 1.2,
  days_overdue: 0.8,
  po_number: 1,
  transaction_type: 0.8,
  disputed: 0.7,
};

// ---- Style factory --------------------------------------------------------

function createStyles(primaryColor: string, margin: number, fontRegular: string, fontBold: string) {
  const mmToPt = (mm: number) => mm * 2.835;
  const m = mmToPt(margin);

  return StyleSheet.create({
    page: {
      fontFamily: fontRegular,
      fontSize: 9,
      color: '#1f2937',
      paddingTop: m,
      paddingBottom: m,
      paddingLeft: m,
      paddingRight: m,
    },
    // Header
    headerContainer: {
      flexDirection: 'row',
      marginBottom: 6,
    },
    headerCol: {
      flex: 1,
      flexDirection: 'column',
    },
    headerColCenter: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'center',
    },
    headerColRight: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'flex-end',
    },
    headerCompanyName: {
      fontSize: 13,
      fontFamily: fontBold,
      color: primaryColor,
      marginBottom: 2,
    },
    headerCompanyAddress: {
      fontSize: 8,
      color: '#6b7280',
      lineHeight: 1.4,
    },
    documentTitle: {
      fontSize: 16,
      fontFamily: fontBold,
      color: primaryColor,
      marginBottom: 10,
      textAlign: 'center',
    },
    divider: {
      marginBottom: 8,
    },
    // Customer address
    section: {
      marginBottom: 8,
    },
    customerAddress: {
      fontSize: 9,
      lineHeight: 1.5,
    },
    // Reference block
    refBlock: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: 8,
      backgroundColor: '#f9fafb',
      padding: 6,
      borderRadius: 3,
    },
    refItem: {
      width: '50%',
      marginBottom: 2,
    },
    refLabel: {
      fontSize: 8,
      color: '#6b7280',
    },
    refValue: {
      fontSize: 9,
      fontFamily: fontBold,
    },
    // Text blocks (intro, payment instructions, closing)
    textBlock: {
      fontSize: 9,
      lineHeight: 1.5,
      marginBottom: 8,
      color: '#374151',
    },
    // Account summary
    accountSummaryRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: 8,
      borderWidth: 1,
      borderColor: '#e5e7eb',
      borderRadius: 3,
    },
    accountSummaryItem: {
      width: '50%',
      padding: 6,
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
    },
    accountSummaryLabel: {
      fontSize: 7,
      color: '#6b7280',
      marginBottom: 1,
    },
    accountSummaryValue: {
      fontSize: 9,
      fontFamily: fontBold,
    },
    // Transaction table
    tableContainer: {
      marginBottom: 8,
    },
    tableHeaderRow: {
      flexDirection: 'row',
      backgroundColor: primaryColor,
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
    },
    tableRowAlt: {
      backgroundColor: '#f9fafb',
    },
    tableRowOverdue: {
      backgroundColor: '#fef3c7',
    },
    tableCell: {
      padding: 4,
      overflow: 'hidden',
    },
    tableHeaderText: {
      fontSize: 7,
      color: '#ffffff',
      fontFamily: fontBold,
    },
    tableCellText: {
      fontSize: 8,
      color: '#374151',
    },
    tableCellTextBold: {
      fontSize: 8,
      color: '#374151',
      fontFamily: fontBold,
    },
    // Totals
    totalsContainer: {
      marginBottom: 8,
    },
    totalsRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 2,
    },
    totalsLabel: {
      fontSize: 8,
      color: '#6b7280',
    },
    totalsValue: {
      fontSize: 8,
      fontFamily: fontBold,
    },
    totalsDivider: {
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
      marginVertical: 2,
    },
    // Ageing summary
    ageingContainer: {
      marginBottom: 8,
      overflow: 'hidden',
    },
    ageingSectionTitle: {
      fontSize: 9,
      fontFamily: fontBold,
      marginBottom: 4,
      color: '#374151',
    },
    ageingHeaderRow: {
      flexDirection: 'row',
      backgroundColor: primaryColor,
    },
    ageingRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
    },
    ageingCell: {
      flex: 1,
      padding: 4,
    },
    ageingHeaderText: {
      fontSize: 7,
      color: '#ffffff',
      fontFamily: fontBold,
    },
    ageingCellText: {
      fontSize: 8,
      color: '#374151',
    },
    ageingListRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 2,
      borderBottomWidth: 1,
      borderBottomColor: '#f3f4f6',
    },
    ageingListLabel: {
      fontSize: 8,
      color: '#6b7280',
    },
    ageingListValue: {
      fontSize: 8,
      fontFamily: fontBold,
    },
    // Section title
    sectionTitle: {
      fontSize: 9,
      fontFamily: fontBold,
      marginBottom: 4,
      color: '#374151',
    },
    // Footer
    footer: {
      position: 'absolute',
      bottom: m,
      left: m,
      right: m,
      paddingTop: 4,
    },
    footerText: {
      fontSize: 7,
      color: '#9ca3af',
    },
    // Page numbers
    pageNumber: {
      position: 'absolute',
      fontSize: 7,
      color: '#9ca3af',
    },
  });
}

// ---- Sub-components -------------------------------------------------------

function HeaderItem({
  item,
  config,
  data,
  styles,
}: Readonly<{
  item: string;
  config: TemplateBuilderConfig;
  data: StatementDocumentData;
  styles: ReturnType<typeof createStyles>;
}>) {
  switch (item) {
    case 'logo':
      if (config.logoUrl) {
        const h = LOGO_HEIGHT[config.logoSize] ?? 36;
        return <Image src={config.logoUrl} style={{ height: h, objectFit: 'contain', marginBottom: 4 }} />;
      }
      return null;
    case 'company-name':
      return <Text style={styles.headerCompanyName}>{config.companyName}</Text>;
    case 'company-address':
      return (
        <Text style={styles.headerCompanyAddress}>
          {[data.companyAddress, data.companyCountry].filter(Boolean).join('\n')}
        </Text>
      );
    default:
      return null;
  }
}

function HeaderColumn({
  items,
  align,
  config,
  data,
  styles,
}: Readonly<{
  items: string[];
  align: 'left' | 'center' | 'right';
  config: TemplateBuilderConfig;
  data: StatementDocumentData;
  styles: ReturnType<typeof createStyles>;
}>) {
  const nonEmpty = items.filter((i) => i !== 'empty');
  if (nonEmpty.length === 0) return <View style={styles.headerCol} />;

  const colStyle =
    align === 'center' ? styles.headerColCenter : align === 'right' ? styles.headerColRight : styles.headerCol;
  return (
    <View style={colStyle}>
      {nonEmpty.map((item, idx) => (
        <HeaderItem key={`${item}-${idx}`} item={item} config={config} data={data} styles={styles} />
      ))}
    </View>
  );
}

function ReferenceBlock({
  data,
  config,
  styles,
}: Readonly<{
  data: StatementDocumentData;
  config: TemplateBuilderConfig;
  styles: ReturnType<typeof createStyles>;
}>) {
  const { items } = config.contentSettings.referenceBlock;
  const entries: [string, string][] = [];
  if (items.accountNumber) entries.push(['Account Number', data.accountNumber]);
  if (items.currencyCode) entries.push(['Currency', data.currencyCode]);
  if (items.paymentTerms) entries.push(['Payment Terms', data.paymentTerms]);
  if (items.statementDate) entries.push(['Statement Date', data.statementDate]);

  if (entries.length === 0) return null;
  return (
    <View style={styles.refBlock}>
      {entries.map(([label, value]) => (
        <View key={label} style={styles.refItem}>
          <Text style={styles.refLabel}>{label}</Text>
          <Text style={styles.refValue}>{value}</Text>
        </View>
      ))}
    </View>
  );
}

function AccountSummarySection({
  data,
  config,
  styles,
}: Readonly<{
  data: StatementDocumentData;
  config: TemplateBuilderConfig;
  styles: ReturnType<typeof createStyles>;
}>) {
  const { items } = config.fieldsSettings.accountSummary;
  const enabled = ACCOUNT_SUMMARY_KEYS.filter((k) => items[k]);
  if (enabled.length === 0) return null;

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Account Summary</Text>
      <View style={styles.accountSummaryRow}>
        {enabled.map((key, idx) => {
          const totalRows = Math.ceil(enabled.length / 2);
          const itemRow = Math.floor(idx / 2);
          const isLastRow = itemRow === totalRows - 1;
          return (
            <View key={key} style={[styles.accountSummaryItem, isLastRow ? { borderBottomWidth: 0 } : {}]}>
              <Text style={styles.accountSummaryLabel}>{ACCOUNT_SUMMARY_LABELS[key]}</Text>
              <Text style={styles.accountSummaryValue}>{String(data[ACCOUNT_SUMMARY_DATA_KEYS[key]])}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function TransactionTable({
  data,
  config,
  styles,
}: Readonly<{
  data: StatementDocumentData;
  config: TemplateBuilderConfig;
  styles: ReturnType<typeof createStyles>;
}>) {
  const enabledColumns = config.fieldsSettings.transactionTable.columns.filter((c) => c.enabled);
  const { zebraStripe, showBorders, highlightOverdue, boldOverdue } =
    config.fieldsSettings.transactionTable.displayOptions;

  if (enabledColumns.length === 0) return null;

  const MONETARY_COLUMNS = ['invoice_amount', 'balance_outstanding'];
  const CENTRE_COLUMNS = ['invoice_date', 'due_date', 'disputed'];

  return (
    <View style={styles.tableContainer}>
      <View style={styles.tableHeaderRow}>
        {enabledColumns.map((col, idx) => {
          const isLastCol = idx === enabledColumns.length - 1;
          const cellBorderStyle = showBorders && !isLastCol ? { borderRightWidth: 1, borderRightColor: '#e5e7eb' } : {};
          const isMonetary = MONETARY_COLUMNS.includes(col.id);
          const isCentred = CENTRE_COLUMNS.includes(col.id);
          const alignStyle = isMonetary
            ? { textAlign: 'right' as const }
            : isCentred
              ? { textAlign: 'center' as const }
              : {};
          return (
            <View
              key={`${idx}-${col.id}`}
              style={[styles.tableCell, { flex: COLUMN_FLEX[col.id] ?? 1 }, cellBorderStyle]}
            >
              <Text style={[styles.tableHeaderText, alignStyle]}>{col.name}</Text>
            </View>
          );
        })}
      </View>
      {data.transactions.map((row, rowIdx) => {
        const isOverdue = data.transactionOverdue[rowIdx] ?? false;
        // Priority: overdue highlight > zebra stripe
        const rowBgStyle =
          isOverdue && highlightOverdue
            ? styles.tableRowOverdue
            : zebraStripe && rowIdx % 2 === 1
              ? styles.tableRowAlt
              : {};
        const cellTextStyle = isOverdue && boldOverdue ? styles.tableCellTextBold : styles.tableCellText;

        return (
          <View key={rowIdx} wrap={false} style={[styles.tableRow, rowBgStyle]}>
            {row.map((cell, cellIdx) => {
              const colId = enabledColumns[cellIdx]?.id;
              const isLastCol = cellIdx === enabledColumns.length - 1;
              const cellBorderStyle =
                showBorders && !isLastCol ? { borderRightWidth: 1, borderRightColor: '#e5e7eb' } : {};
              const isMonetary = MONETARY_COLUMNS.includes(colId ?? '');
              const isCentred = CENTRE_COLUMNS.includes(colId ?? '');
              const alignStyle = isMonetary
                ? { textAlign: 'right' as const }
                : isCentred
                  ? { textAlign: 'center' as const }
                  : {};
              return (
                <View
                  key={cellIdx}
                  style={[styles.tableCell, { flex: COLUMN_FLEX[colId ?? ''] ?? 1 }, cellBorderStyle]}
                >
                  <Text style={[cellTextStyle, alignStyle]}>{cell}</Text>
                </View>
              );
            })}
          </View>
        );
      })}
    </View>
  );
}

function TotalsSection({
  data,
  config,
  styles,
}: Readonly<{
  data: StatementDocumentData;
  config: TemplateBuilderConfig;
  styles: ReturnType<typeof createStyles>;
}>) {
  const { items, alignment } = config.fieldsSettings.totals;
  const enabled = TOTALS_KEYS.filter((k) => items[k]);
  if (enabled.length === 0) return null;

  const isRight = alignment !== 'full';
  const containerAlign = isRight ? ({ alignItems: 'flex-end' } as const) : {};
  const rowWidth = isRight ? { width: 200 } : {};

  return (
    <View style={[styles.totalsContainer, containerAlign]}>
      <View style={[styles.totalsDivider, rowWidth]} />
      {enabled.map((key) => (
        <View key={key} style={[styles.totalsRow, rowWidth]}>
          <Text style={styles.totalsLabel}>{TOTAL_LABELS[key]}</Text>
          <Text style={styles.totalsValue}>{String(data[TOTAL_DATA_KEYS[key]])}</Text>
        </View>
      ))}
    </View>
  );
}

function AgeingSummarySection({
  data,
  config,
  styles,
}: Readonly<{
  data: StatementDocumentData;
  config: TemplateBuilderConfig;
  styles: ReturnType<typeof createStyles>;
}>) {
  const { displayStyle, highlightOverdue } = config.fieldsSettings.agingSummary;

  if (data.agingBuckets.length === 0) return null;

  // List style: vertical label/value pairs
  if (displayStyle === 'list') {
    return (
      <View wrap={false} style={styles.ageingContainer}>
        <Text style={styles.ageingSectionTitle}>Ageing Summary</Text>
        {data.agingBuckets.map((val, idx) => {
          const label = data.agingBucketLabels?.[idx] ?? `Band ${idx + 1}`;
          const isOverdueBucket = data.agingBucketOverdue?.[idx] ?? true;
          return (
            <View
              key={label}
              style={[styles.ageingListRow, isOverdueBucket && highlightOverdue ? { backgroundColor: '#fef3c7' } : {}]}
            >
              <Text style={styles.ageingListLabel}>{label}</Text>
              <Text style={styles.ageingListValue}>{val}</Text>
            </View>
          );
        })}
      </View>
    );
  }

  // Table style (default)
  return (
    <View wrap={false} style={styles.ageingContainer}>
      <Text style={styles.ageingSectionTitle}>Ageing Summary</Text>
      <View style={styles.ageingHeaderRow}>
        {data.agingBuckets.map((_val, idx) => {
          const label = data.agingBucketLabels?.[idx] ?? `Band ${idx + 1}`;
          return (
            <View key={label} style={styles.ageingCell}>
              <Text style={styles.ageingHeaderText}>{label}</Text>
            </View>
          );
        })}
      </View>
      <View style={styles.ageingRow}>
        {data.agingBuckets.map((val, idx) => {
          const label = data.agingBucketLabels?.[idx] ?? `Band ${idx + 1}`;
          const isOverdueBucket = data.agingBucketOverdue?.[idx] ?? true;
          return (
            <View
              key={label}
              style={[styles.ageingCell, isOverdueBucket && highlightOverdue ? { backgroundColor: '#fef3c7' } : {}]}
            >
              <Text style={styles.ageingCellText}>{val}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function FooterSection({
  config,
  styles,
}: Readonly<{
  config: TemplateBuilderConfig;
  styles: ReturnType<typeof createStyles>;
}>) {
  const { footer } = config.layoutSettings;
  if (!footer?.enabled) return null;

  const lines: string[] = [];
  if (footer.showCompanyContact) {
    lines.push(config.companyName);
    const contact: string[] = [];
    if (config.companyEmail) contact.push(config.companyEmail);
    if (config.companyPhone) contact.push(config.companyPhone);
    if (contact.length) lines.push(contact.join(' | '));
  }
  const reg: string[] = [];
  if (footer.showCompanyReg && config.companyReg) reg.push(`Reg No: ${config.companyReg}`);
  if (footer.showVatNumber && config.companyVat) reg.push(`VAT No: ${config.companyVat}`);
  if (reg.length) lines.push(reg.join(' | '));

  if (lines.length === 0) return null;

  const textAlign = (footer.alignment ?? 'center') as 'left' | 'center' | 'right';

  const dividerStyle = footer.showDivider
    ? {
        borderTopWidth: Number(footer.dividerThickness ?? '1'),
        borderTopColor: footer.dividerColor || '#e5e7eb',
      }
    : {};

  return (
    <View fixed={footer.repeatOnAllPages} style={[styles.footer, dividerStyle]}>
      {lines.map((line, idx) => (
        <Text key={idx} style={[styles.footerText, { textAlign }]}>
          {line}
        </Text>
      ))}
    </View>
  );
}

function PageNumberSection({
  config,
  styles,
  margin,
}: Readonly<{
  config: TemplateBuilderConfig;
  styles: ReturnType<typeof createStyles>;
  margin: number;
}>) {
  const { pageNumbers } = config.layoutSettings;
  if (!pageNumbers?.enabled) return null;

  const mmToPt = (mm: number) => mm * 2.835;
  const m = mmToPt(margin);

  const positionStyle = (() => {
    switch (pageNumbers.position) {
      case 'bottom-left':
        return { bottom: m * 0.35, left: m, textAlign: 'left' as const };
      case 'bottom-right':
        return { bottom: m * 0.35, right: m, textAlign: 'right' as const };
      case 'top-right':
        return { top: m * 0.35, right: m, textAlign: 'right' as const };
      default:
        return {
          bottom: m * 0.35,
          left: m,
          right: m,
          textAlign: 'center' as const,
        };
    }
  })();

  return (
    <Text
      fixed
      style={[styles.pageNumber, positionStyle]}
      render={({ pageNumber, totalPages }) => {
        switch (pageNumbers.format) {
          case 'slash':
            return `${pageNumber}/${totalPages}`;
          case 'number':
            return String(pageNumber);
          default:
            return `Page ${pageNumber} of ${totalPages}`;
        }
      }}
    />
  );
}

// ---- Main component -------------------------------------------------------

interface StatementDocumentProps {
  config: TemplateBuilderConfig;
  data: StatementDocumentData;
}

export function StatementDocument({ config, data }: Readonly<StatementDocumentProps>) {
  const { layoutSettings, contentSettings, fieldsSettings } = config;
  const margin = MARGIN_MM[layoutSettings.margins] ?? 25;
  const { regular: fontRegular, bold: fontBold } = mapFont(config.documentFont);
  const styles = createStyles(config.primaryColor, margin, fontRegular, fontBold);

  const { left, center, right, showDivider } = layoutSettings.headerLayout;
  const hasHeader = [...left, ...center, ...right].some((i) => i !== 'empty');

  // Extra padding-bottom for footer
  const footerDividerThickness = Number(layoutSettings.footer?.dividerThickness ?? '1');
  const footerLineCount = 3;
  const footerReserve = layoutSettings.footer?.enabled ? footerLineCount * 10 + footerDividerThickness + 8 : 0;

  // Header divider inline styles (thickness + custom colour)
  const headerDividerStyle = {
    borderBottomWidth: Number(layoutSettings.headerLayout.dividerThickness ?? '1'),
    borderBottomColor: layoutSettings.headerLayout.dividerColor || config.primaryColor,
    marginBottom: 8,
  };

  return (
    <Document>
      <Page
        size={PAGE_SIZE_MAP[layoutSettings.pageSize] as 'A4' | 'A5' | 'LETTER'}
        orientation={layoutSettings.orientation}
        style={[styles.page, { paddingBottom: styles.page.paddingBottom + footerReserve }]}
      >
        {/* Header */}
        {hasHeader && (
          <View fixed={layoutSettings.headerLayout.repeatOnAllPages} style={styles.headerContainer}>
            <HeaderColumn items={left} align="left" config={config} data={data} styles={styles} />
            <HeaderColumn items={center} align="center" config={config} data={data} styles={styles} />
            <HeaderColumn items={right} align="right" config={config} data={data} styles={styles} />
          </View>
        )}
        {showDivider && (
          <View fixed={layoutSettings.headerLayout.repeatOnAllPages} style={[styles.divider, headerDividerStyle]} />
        )}

        {/* Document title */}
        {data.documentTitle && <Text style={styles.documentTitle}>{data.documentTitle}</Text>}

        {/* Body */}
        {contentSettings.customerAddress.enabled && data.customerAddressContent && (
          <View style={styles.section}>
            <Text style={styles.customerAddress}>{data.customerAddressContent}</Text>
          </View>
        )}

        {contentSettings.introText.enabled && data.introText && <Text style={styles.textBlock}>{data.introText}</Text>}

        {contentSettings.referenceBlock.enabled && <ReferenceBlock data={data} config={config} styles={styles} />}

        {fieldsSettings.accountSummary.enabled && <AccountSummarySection data={data} config={config} styles={styles} />}

        <TransactionTable data={data} config={config} styles={styles} />

        <TotalsSection data={data} config={config} styles={styles} />

        {fieldsSettings.agingSummary.enabled && <AgeingSummarySection data={data} config={config} styles={styles} />}

        {contentSettings.paymentInstructions.enabled && data.paymentInstructions && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Payment Instructions</Text>
            <Text style={styles.textBlock}>{data.paymentInstructions}</Text>
          </View>
        )}

        {contentSettings.closingText.enabled && data.closingText && (
          <Text style={styles.textBlock}>{data.closingText}</Text>
        )}

        {/* Footer — fixed positions it at the bottom of every page when repeatOnAllPages is on */}
        <FooterSection config={config} styles={styles} />

        {/* Page numbers */}
        <PageNumberSection config={config} styles={styles} margin={margin} />
      </Page>
    </Document>
  );
}
